import PropTypes from 'prop-types';
import { useEffect } from 'react';

import CouponsList from 'components/Coupons/CouponsList';
import FlatLayout from 'components/Layout/FlatLayout';

import { couponQueryKeys } from 'lib/queryKeys';
import { sendSupixEvent } from 'lib/supix';

import { useUtm } from 'providers/UtmProvider';

import { getCouponsList } from 'services/coupon';

import Breadcrumb from './Breadcrumb';
import CouponsHead from './Head';
import HighlightedCouponsStores from './HighlightedCouponsStores';
import CouponsPageDescription from './PageDescription';
import PopularCouponsStores from './PopularCouponsStores';
import CouponsSchema from './Schema';
import StoreWithCouponsSearchBox from './StoreWithCouponsSearchBox';

import COUPONS from 'constants/coupons';

const GA_EVENT_CATEGORY = 'home_coupons';

const Coupons = ({
  serverCoupons,
  featuredStores = [],
  popularStores = [],
}) => {
  const { isUtmLoaded } = useUtm();

  useEffect(() => {
    if (isUtmLoaded) {
      sendSupixEvent('/cp', { id: 0 });
    }
  }, [isUtmLoaded]);

  return (
    <>
      <FlatLayout.Margin>
        <FlatLayout.Heading>
          <CouponsHead />
          <CouponsSchema />
          <Breadcrumb />
        </FlatLayout.Heading>
        <FlatLayout.Body className="gap-10 lg:gap-12">
          <PopularCouponsStores
            gaEventCategory={GA_EVENT_CATEGORY}
            stores={popularStores}
          />
          <StoreWithCouponsSearchBox gaEventCategory={GA_EVENT_CATEGORY} />
          <CouponsList
            gaEventCategory={GA_EVENT_CATEGORY}
            initialCoupons={serverCoupons}
            queryFn={({ page }, opts) =>
              getCouponsList(
                { limit: COUPONS.COUPONS_LIMIT, after: page, sort: 'best' },
                opts
              )
            }
            queryKey={couponQueryKeys.list({
              sortType: 'best',
            })}
            withCouponStoreCover
          />
          {featuredStores && (
            <HighlightedCouponsStores stores={featuredStores} />
          )}
          <CouponsPageDescription />
        </FlatLayout.Body>
      </FlatLayout.Margin>
      <FlatLayout.Footer />
    </>
  );
};

Coupons.propTypes = {
  featuredStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeImage: PropTypes.string.isRequired,
      storeSlug: PropTypes.string.isRequired,
      storeName: PropTypes.string.isRequired,
    })
  ),
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired),
        couponPublished: PropTypes.string,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  popularStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeName: PropTypes.string,
      storeImage: PropTypes.string,
      storeSlug: PropTypes.string,
      totalCoupons: PropTypes.number,
    })
  ),
};

export default Coupons;
